// src/components/Separation/SettlementModal.tsx

import React, { useState } from 'react';
import { toast } from 'sonner';
import { SettlementCreate } from '../../types/separation';
import { createSettlement } from '../../api/separation';
import RetainerReleaseCard from './retainerreleasecard';

interface SettlementModalProps {
  isOpen: boolean;
  onClose: () => void;
  separationType: string;
  separationId: string;
  employeeId: string;
  onSuccess: () => void;
}

const SettlementModal: React.FC<SettlementModalProps> = ({
  isOpen,
  onClose,
  separationType,
  separationId,
  employeeId,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [createdSettlementId, setCreatedSettlementId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    pending_salary: 0,
    leave_encashment: 0,
    gratuity: 0,
    bonus_due: 0,
    notice_pay: 0,
    other_earnings: 0,
    loan_recovery: 0,
    advance_recovery: 0,
    notice_period_deduction: 0,
    other_deductions: 0,
    notes: '',
  });

  if (!isOpen) return null;

  const grossEarnings = 
    formData.pending_salary +
    formData.leave_encashment +
    formData.gratuity +
    formData.bonus_due +
    formData.notice_pay +
    formData.other_earnings;

  const totalDeductions =
    formData.loan_recovery +
    formData.advance_recovery +
    formData.notice_period_deduction +
    formData.other_deductions;

  const netPayable = grossEarnings - totalDeductions;

  const created = !!createdSettlementId;

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const payload: SettlementCreate = {
        employee_id: employeeId,
        separation_type: separationType,
        separation_id: separationId,
        ...formData,
      };
      const res = await createSettlement(payload);
      // Keep the modal open so the admin can release/hold the retained balance
      // against the freshly-created F&F settlement.
      setCreatedSettlementId(res?.data?.id ?? null);
      toast.success('Settlement created');
    } catch (error: any) {
      toast.error(error.message || 'Could not create settlement');
    } finally {
      setLoading(false);
    }
  };

  const handleDone = () => {
    onSuccess();
    onClose();
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: parseFloat(value) || 0 }));
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-500 opacity-75" onClick={onClose}></div>
        
        <div className="relative bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold">Full & Final Settlement</h3>
            <p className="text-sm text-gray-500 mt-1">
              {separationType} - {separationId.slice(0, 8)}...
            </p>
          </div>

          <div className="p-6">
            {created && (
              <div className="bg-gray-50 rounded-lg p-4" style={{ marginBottom: '1rem' }}>
                <p className="text-sm text-green-600 font-medium">
                  ✓ Settlement created — Net payable ₹{netPayable.toLocaleString()}
                </p>
              </div>
            )}

            {/* Retained balance (retainer-ship) — release or hold in the F&F */}
            <RetainerReleaseCard
              employeeId={employeeId}
              settlementId={createdSettlementId ?? undefined}
            />

            <fieldset disabled={created} style={{ border: 'none', margin: 0, padding: 0, minWidth: 0 }}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Earnings Section */}
              <div className="border rounded-lg p-4">
                <h4 className="font-semibold text-green-600 mb-3">Earnings</h4>
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Pending Salary</label>
                    <input
                      type="number"
                      value={formData.pending_salary}
                      onChange={(e) => handleInputChange('pending_salary', e.target.value)}
                      className="mt-1 w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Leave Encashment</label>
                    <input
                      type="number"
                      value={formData.leave_encashment}
                      onChange={(e) => handleInputChange('leave_encashment', e.target.value)}
                      className="mt-1 w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Gratuity</label>
                    <input
                      type="number"
                      value={formData.gratuity}
                      onChange={(e) => handleInputChange('gratuity', e.target.value)}
                      className="mt-1 w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Bonus Due</label>
                    <input
                      type="number"
                      value={formData.bonus_due}
                      onChange={(e) => handleInputChange('bonus_due', e.target.value)}
                      className="mt-1 w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Notice Pay</label>
                    <input
                      type="number"
                      value={formData.notice_pay}
                      onChange={(e) => handleInputChange('notice_pay', e.target.value)}
                      className="mt-1 w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Other Earnings</label>
                    <input
                      type="number"
                      value={formData.other_earnings}
                      onChange={(e) => handleInputChange('other_earnings', e.target.value)}
                      className="mt-1 w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                </div>
              </div>

              {/* Deductions Section */}
              <div className="border rounded-lg p-4">
                <h4 className="font-semibold text-red-600 mb-3">Deductions</h4>
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Loan Recovery</label>
                    <input
                      type="number"
                      value={formData.loan_recovery}
                      onChange={(e) => handleInputChange('loan_recovery', e.target.value)}
                      className="mt-1 w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Advance Recovery</label>
                    <input
                      type="number"
                      value={formData.advance_recovery}
                      onChange={(e) => handleInputChange('advance_recovery', e.target.value)}
                      className="mt-1 w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Notice Period Deduction</label>
                    <input
                      type="number"
                      value={formData.notice_period_deduction}
                      onChange={(e) => handleInputChange('notice_period_deduction', e.target.value)}
                      className="mt-1 w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Other Deductions</label>
                    <input
                      type="number"
                      value={formData.other_deductions}
                      onChange={(e) => handleInputChange('other_deductions', e.target.value)}
                      className="mt-1 w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Summary */}
            <div className="mt-6 bg-gray-50 rounded-lg p-4">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-sm text-gray-500">Gross Earnings</p>
                  <p className="text-xl font-bold text-green-600">₹{grossEarnings.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Deductions</p>
                  <p className="text-xl font-bold text-red-600">₹{totalDeductions.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Net Payable</p>
                  <p className="text-xl font-bold text-blue-600">₹{netPayable.toLocaleString()}</p>
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700">Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                rows={3}
                className="mt-1 w-full px-3 py-2 border rounded-lg"
                placeholder="Add any additional notes..."
              />
            </div>
            </fieldset>
          </div>

          <div className="px-6 py-4 border-t border-gray-200 flex gap-3">
            {created ? (
              <button
                onClick={handleDone}
                className="flex-1 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Done
              </button>
            ) : (
              <>
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="flex-1 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  style={{ opacity: loading ? 0.6 : 1 }}
                >
                  {loading ? 'Creating...' : 'Create Settlement'}
                </button>
                <button onClick={onClose} className="flex-1 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettlementModal;